/**
 * CharterPrepSidebar — Phase-1 "Operations" sidebar content.
 *
 * Section progress + (after prep completes) a Profile / Sharing two-tab panel.
 * Reads charter prep WS state from Redux; owns only its own sub-tab + modal UI
 * state. Shared "sharing mode selected" state lives in Redux so the Approve
 * button in CharterPrepBody (a separate component tree) stays in sync.
 */
import { useEffect, useState } from 'react'
import { CheckCircle2, Share2, User } from 'lucide-react'
import { Separator } from '@/components/ui/separator'
import { cn } from '@/lib/utils'
import { useAppDispatch, useAppSelector } from '@/redux/hooks'
import {
  selectPrepProfile,
  selectCompletedSections,
  selectActiveSectionKey,
  selectProfileApprovalRequired,
  selectSharingMode,
  selectSharingModeSelected,
  selectVisibleSections,
  selectSectionOrder,
  selectProfileApproved,
  selectPrepStatus,
  selectMyPrepProfile,
} from '../../redux/selectors/charterSelectors'
import { DEFAULT_SECTION_ORDER } from '../../utils/charterDisplayMessages'
import {
  setSharingMode as setSharingModeAction,
  setVisibleSections as setVisibleSectionsAction,
} from '../../redux/slices/charterPrepWsSlice'
import {
  setSharingSectionMode,
  setPrepVisibleSections,
} from '../../redux/asyncThunks/charterWsThunks'
import { SectionProgressBar } from '../Phase1/SectionProgressBar'
import { SharingModeSelector } from '../Phase1/SharingModeSelector'
import { PrepProfileCard } from '@/components/OGE/Sidebar/PrepProfileCard'
import { PrepProfileModal } from '@/components/OGE/Sidebar/PrepProfileModal'
import type {
  SharingMode,
  StructuredProfile,
} from '../../redux/types/charterTypes'

export function CharterPrepSidebar() {
  const dispatch = useAppDispatch()

  const prepProfileWs = useAppSelector(selectPrepProfile)
  const completedSectionsWs = useAppSelector(selectCompletedSections)
  const activeSectionKey = useAppSelector(selectActiveSectionKey)
  const profileApprovalRequired = useAppSelector(selectProfileApprovalRequired)
  const sharingModeWs = useAppSelector(selectSharingMode)
  const sharingModeSelected = useAppSelector(selectSharingModeSelected)
  const visibleSections = useAppSelector(selectVisibleSections)
  const sectionOrderWs = useAppSelector(selectSectionOrder)
  const profileApprovedWs = useAppSelector(selectProfileApproved)
  const prepStatus = useAppSelector(selectPrepStatus)
  const myProfile = useAppSelector(selectMyPrepProfile)

  // The prep WS slice is only populated while the prep socket is connected.
  // When this sidebar is shown as the read-only Phase-1 reference beside the
  // group session, only the REST profile is available — fall back to it so the
  // panel isn't blank.
  const completedSections =
    Object.keys(completedSectionsWs).length > 0
      ? completedSectionsWs
      : (myProfile?.completed_sections ?? {})
  const sectionOrder =
    sectionOrderWs.length > 0 ? sectionOrderWs : DEFAULT_SECTION_ORDER
  const sharingMode = sharingModeWs || myProfile?.sharing_mode || 'auto'
  const profileApproved = profileApprovedWs || myProfile?.profile_approved || false

  const [sidebarTab, setSidebarTab] = useState<'profile' | 'sharing'>('profile')
  const [isProfileModalOpen, setIsProfileModalOpen] = useState(false)

  const isPrepComplete = profileApprovalRequired || prepStatus === 'completed'
  const structuredProfile = (prepProfileWs ??
    myProfile?.structured_profile ??
    null) as unknown as StructuredProfile | null

  useEffect(() => {
    if (isPrepComplete) {
      setSidebarTab('sharing')
    }
  }, [isPrepComplete])

  const handleSharingModeChange = (mode: SharingMode) => {
    dispatch(setSharingModeAction(mode))
    dispatch(setSharingSectionMode({ sharing_mode: mode }))
  }

  const handleVisibleSectionsToggle = (key: string) => {
    const next = visibleSections.includes(key)
      ? visibleSections.filter((k) => k !== key)
      : [...visibleSections, key]
    dispatch(setVisibleSectionsAction(next))
    dispatch(setPrepVisibleSections({ visible_sections: next }))
  }

  return (
    <div className='px-2 space-y-4'>
      {/* Progress always visible */}
      <div>
        <p className='text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2 px-1'>
          Your Progress
        </p>
        <SectionProgressBar
          sectionOrder={sectionOrder}
          completedSections={completedSections}
          activeSectionKey={activeSectionKey}
        />
      </div>

      {/* After prep completes: two-tab panel */}
      {isPrepComplete && (
        <>
          <Separator />

          <div className='flex gap-1'>
            <button
              onClick={() => setSidebarTab('profile')}
              className={cn(
                'flex-1 h-7 rounded-md text-xs font-semibold flex items-center justify-center gap-1 transition-all',
                sidebarTab === 'profile'
                  ? 'bg-[#E6ECF2] text-slate-900 border border-[#B9C8D8] shadow-sm'
                  : 'text-slate-500 border border-transparent hover:bg-[#EEF2F7]'
              )}
            >
              <User className='w-3 h-3' />
              Profile
            </button>
            <button
              onClick={() => setSidebarTab('sharing')}
              className={cn(
                'flex-1 h-7 rounded-md text-xs font-semibold flex items-center justify-center gap-1.5 transition-all',
                sidebarTab === 'sharing'
                  ? 'bg-[#E6ECF2] text-slate-900 border border-[#B9C8D8] shadow-sm'
                  : 'text-slate-500 border border-transparent hover:bg-[#EEF2F7]'
              )}
            >
              <Share2 className='w-3 h-3' />
              Sharing
              {!sharingModeSelected && !profileApproved && (
                <span className='w-1.5 h-1.5 rounded-full bg-amber-400 flex-shrink-0' />
              )}
            </button>
          </div>

          {/* Profile tab */}
          {sidebarTab === 'profile' && structuredProfile && (
            <PrepProfileCard
              profile={structuredProfile}
              sharingMode={sharingMode}
              profileApproved={profileApproved}
              onClick={() => setIsProfileModalOpen(true)}
            />
          )}

          {/* Sharing tab */}
          {sidebarTab === 'sharing' && (
            <div className='space-y-3'>
              <SharingModeSelector
                currentMode={sharingMode as SharingMode}
                onChange={profileApproved ? () => {} : handleSharingModeChange}
                disabled={profileApproved}
              />

              {sharingMode === 'selective' && sectionOrder.length > 0 && (
                <div className='flex flex-wrap gap-1'>
                  {sectionOrder.map((key) => (
                    <button
                      key={key}
                      disabled={profileApproved}
                      onClick={() =>
                        !profileApproved && handleVisibleSectionsToggle(key)
                      }
                      className={cn(
                        'px-2 py-0.5 rounded-full border text-xs transition-colors',
                        visibleSections.includes(key)
                          ? 'bg-indigo-100 border-indigo-300 text-indigo-700'
                          : 'bg-white border-gray-200 text-gray-500',
                        profileApproved && 'opacity-60 cursor-default'
                      )}
                    >
                      {key.replace(/_/g, ' ')}
                    </button>
                  ))}
                </div>
              )}

              {sharingModeSelected && structuredProfile && (
                <div className='rounded-lg border border-slate-100 bg-slate-50 p-3 space-y-1'>
                  <p className='text-[10px] font-semibold uppercase tracking-wide text-gray-400 mb-1'>
                    What teammates will see
                  </p>
                  {sharingMode === 'manual' ? (
                    <p className='text-xs text-gray-500 italic'>
                      Nothing will be shared with your team.
                    </p>
                  ) : sharingMode === 'auto' ? (
                    <p className='text-xs text-gray-600 leading-relaxed'>
                      {structuredProfile.shareable_summary || '—'}
                    </p>
                  ) : visibleSections.length === 0 ? (
                    <p className='text-xs text-amber-600 italic'>
                      No sections selected — nothing will be shared.
                    </p>
                  ) : (
                    <ul className='space-y-1'>
                      {visibleSections.map((key) => (
                        <li
                          key={key}
                          className='flex items-center gap-1.5 text-xs text-gray-600'
                        >
                          <CheckCircle2 className='h-3 w-3 text-indigo-400 flex-shrink-0' />
                          {key
                            .replace(/_/g, ' ')
                            .replace(/\b\w/g, (c) => c.toUpperCase())}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}

              {!sharingModeSelected && !profileApproved && (
                <p className='text-[10px] text-amber-600 text-center'>
                  Select a sharing preference to continue.
                </p>
              )}
            </div>
          )}
        </>
      )}

      {isPrepComplete && structuredProfile && (
        <PrepProfileModal
          isOpen={isProfileModalOpen}
          onClose={() => setIsProfileModalOpen(false)}
          profile={structuredProfile}
          profileApproved={profileApproved}
        />
      )}
    </div>
  )
}
